import React from "react";
import { Link } from "react-router-dom";
import { ArrowRight, ArrowUpRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { tint } from "@/data/site";
import { Reveal } from "@/components/shared/Reveal";
import { DashRing } from "@/components/shared/Decorations";

export const Container = ({ children, className = "" }) => (
  <div className={cn("mx-auto w-full max-w-6xl px-5 sm:px-6 lg:px-8", className)}>{children}</div>
);

export const Eyebrow = ({ children, className = "" }) => (
  <div className={cn("inline-flex items-center gap-2.5", className)}>
    <span className="dot-pulse h-[7px] w-[7px] flex-shrink-0 rounded-full" style={{ background: "var(--page-accent)" }} />
    <span className="font-tech text-[11px] uppercase tracking-[0.2em]" style={{ color: "var(--page-accent)" }}>
      {children}
    </span>
  </div>
);

export const SectionLabel = ({ index = "01", total = "03", children }) => (
  <Reveal>
    <div className="mb-10 flex items-center gap-4 sm:mb-14">
      <span className="font-display whitespace-nowrap text-[13px] tracking-[0.18em]" style={{ color: "var(--text-2)" }}>
        SIGNAL / {index} — {total}
      </span>
      <div className="h-px flex-1" style={{ background: `linear-gradient(90deg, ${tint(45)} 0%, transparent 100%)` }} />
      {children ? (
        <span className="font-tech hidden text-[11px] uppercase tracking-[0.18em] sm:inline" style={{ color: "var(--text-3)" }}>
          {children}
        </span>
      ) : null}
    </div>
  </Reveal>
);

export const AccentButton = React.forwardRef(
  ({ to, href, children, variant = "solid", className = "", ...props }, ref) => {
    const base =
      "group inline-flex items-center justify-center gap-2 rounded-[12px] px-6 min-h-[48px] text-[12px] font-semibold uppercase tracking-[0.08em] transition-[background-color,box-shadow,transform,color] duration-200 active:scale-[0.98]";
    const style =
      variant === "solid"
        ? { background: "var(--page-accent)", color: "var(--on-accent)", boxShadow: `0 0 28px ${tint(35)}` }
        : variant === "outline"
        ? { background: tint(8), color: "var(--page-accent)", border: `1px solid ${tint(42)}` }
        : { background: "transparent", color: "var(--text-1)", border: "1px solid var(--stroke-1)" };
    const inner = (
      <>
        {children}
        <ArrowRight className="transition-transform duration-200 group-hover:translate-x-0.5" />
      </>
    );
    if (to) return (
      <Link ref={ref} to={to} className={cn(base, className)} style={style} {...props}>{inner}</Link>
    );
    if (href) return (
      <a ref={ref} href={href} className={cn(base, className)} style={style} {...props}>{inner}</a>
    );
    return (
      <button ref={ref} className={cn(base, className)} style={style} {...props}>{inner}</button>
    );
  }
);
AccentButton.displayName = "AccentButton";

export const TextLink = ({ to, children, className = "" }) => (
  <Link
    to={to}
    className={cn(
      "group inline-flex items-center gap-1.5 border-b pb-0.5 text-[13px] tracking-[0.02em] transition-colors",
      className
    )}
    style={{ color: "var(--text-2)", borderColor: "var(--stroke-1)" }}
  >
    {children}
    <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
  </Link>
);

export const Pill = ({ children, className = "", active = false }) => (
  <span
    className={cn("font-tech inline-flex items-center rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.12em]", className)}
    style={
      active
        ? { background: tint(14), color: "var(--page-accent)", border: `1px solid ${tint(45)}` }
        : { background: "var(--surface-0)", color: "var(--text-2)", border: "1px solid var(--stroke-0)" }
    }
  >
    {children}
  </span>
);

export const CircleCTA = ({ to = "/contact", onClick, label = "BOOK A CALL", sub, size = 148 }) => {
  const sharedClassName =
    "group relative grid place-items-center rounded-full text-center transition-transform duration-300 hover:scale-[1.04]";
  const sharedStyle = {
    width: size,
    height: size,
    background: tint(9),
    border: `1px solid ${tint(55)}`,
    boxShadow: `0 0 50px ${tint(14)}, inset 0 0 40px ${tint(5)}`,
  };
  const inner = (
    <>
      <DashRing className="absolute inset-0 h-full w-full animate-spin-slow" style={{ color: "var(--page-accent)" }} />
      <span className="font-display text-[19px] leading-[1.1] tracking-[0.08em] glow-accent" style={{ color: "var(--page-accent)" }}
        dangerouslySetInnerHTML={{ __html: label.replace(" ", "<br/>") }} />
      <ArrowRight className="mt-1.5 h-5 w-5" style={{ color: "var(--page-accent)" }} />
    </>
  );
  return (
    <div className="flex flex-col items-center gap-3">
      {onClick ? (
        <button type="button" onClick={onClick} data-testid="circle-cta" className={sharedClassName} style={sharedStyle}>
          {inner}
        </button>
      ) : (
        <Link to={to} data-testid="circle-cta" className={sharedClassName} style={sharedStyle}>
          {inner}
        </Link>
      )}
      {sub ? <span className="text-center text-[11px] tracking-[0.04em]" style={{ color: "var(--text-3)" }}>{sub}</span> : null}
    </div>
  );
};

export const MetricTile = ({ value, label, testid }) => (
  <div
    className="relative overflow-hidden rounded-[16px] p-5 transition-[transform,border-color] duration-200 hover:-translate-y-0.5"
    style={{ background: "var(--surface-0)", border: "1px solid var(--stroke-0)" }}
  >
    <div className="absolute right-3 top-3"><CornerTick /></div>
    <p className="font-display tabular text-4xl leading-none sm:text-5xl" style={{ color: "var(--text-0)" }} data-testid={testid}>{value}</p>
    <p className="font-tech mt-3 text-[10px] uppercase tracking-[0.14em]" style={{ color: "var(--page-accent)" }}>{label}</p>
  </div>
);

const CornerTick = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" aria-hidden="true">
    <path d="M13 1 H4 M13 1 V10" stroke="currentColor" strokeOpacity="0.3" strokeWidth="1" fill="none" style={{ color: "var(--page-accent)" }} />
  </svg>
);

export const DiamondStat = ({ value, label, warm = false }) => {
  const color = warm ? "#FF6B2B" : "var(--page-accent)";
  return (
    <div
      className="grid h-32 w-32 flex-shrink-0 place-items-center"
      style={{ transform: "rotate(45deg)", background: tint(7, color), border: `1px solid ${tint(28, color)}` }}
    >
      <div className="text-center" style={{ transform: "rotate(-45deg)" }}>
        <p className="font-display text-3xl leading-none" style={{ color: "var(--text-0)" }}>{value}</p>
        <p className="font-tech mt-1 text-[8px] uppercase tracking-[0.12em]" style={{ color: tint(85, color) }}>{label}</p>
      </div>
    </div>
  );
};

export const Marquee = ({ items, className = "" }) => (
  <div className={cn("relative flex overflow-hidden", className)} aria-hidden="true">
    <div className="flex w-max animate-marquee items-center">
      {[...items, ...items].map((it, i) => (
        <span key={i} className="flex items-center">
          <span className="font-tech mx-7 text-[12px] uppercase tracking-[0.18em]" style={{ color: "var(--text-2)" }}>{it}</span>
          <span className="h-1 w-1 rounded-full" style={{ background: "var(--page-accent)" }} />
        </span>
      ))}
    </div>
  </div>
);

export const SurfaceCard = ({ children, className = "", hover = true, style }) => (
  <div
    className={cn(
      "relative rounded-[18px] p-6 transition-[transform,border-color,box-shadow] duration-200",
      hover && "hover:-translate-y-1",
      className
    )}
    style={{ background: "var(--surface-0)", border: "1px solid var(--stroke-0)", boxShadow: "var(--card-shadow)", ...style }}
  >
    {children}
  </div>
);
